// While the intro plays, the hero around it follows the film. The intro
// (public/teaser/index.html, its HOST PAGE section) posts what's on screen
// about 30 times a second: whether it's playing, how lit the picture is, the
// colors of its left, middle and right thirds, the music's energy and beat,
// how busy the orbits should be, and story beats, each with how far off it
// is (the final blast is cued just ahead of time, so the hero's flash lands
// on the same frame as the film's). This turns
// that into four things, all inside the hero and only while the film plays:
//   - the whole page turns dark, the hero's text and the header dim, so the
//     film leads (theme.ts's setThemeOverride; .intro-playing on the hero
//     and on <html>, styles.css) — and a pause brings it all back;
//   - a backlight behind the frame takes the picture's colors, like LEDs
//     behind a TV (.teaser-backlight, fed --intro-l/-c/-r and --intro-light);
//   - the orbit rings start sending probes of light as the film deploys to
//     the cities, the orbit and the Moon: hops between rings, arcs riding
//     them, and links across them once the Earth and the Moon talk;
//   - as the light leaves the solar system, one flash: a wave and rays from
//     the rings out across the whole section.
// Everything eases, nothing runs while the film doesn't play (or is
// fullscreen, or scrolled away), and the canvas is released when idle.
// Reduced motion keeps only the dim and the backlight.

import { useEffect, useRef } from 'react';
import { setThemeOverride } from '../theme';

interface IntroMessage {
  type: 'cloudish-intro';
  playing?: boolean;
  full?: boolean;
  light?: number;
  flash?: number;
  energy?: number;
  beat?: number;
  probes?: number;
  amb?: number[][] | null;
  /** `in`: seconds until it happens (0 or a hair below for beats sent as they happen). */
  events?: { kind: string; in: number }[];
}

// .hero-rings in styles.css: 1100px across, rings inset 220, 90 and 0 — inner to outer.
const RINGS_SIZE = 1100;
const RING_INSETS = [220, 90, 0];
const MAX_PARTS = 140;

type Kind = 'hop' | 'arc' | 'link' | 'ray' | 'wave';
interface Part {
  kind: Kind;
  t0: number;
  dur: number;
  ring0: number;
  ring1: number;
  a0: number;
  a1: number;
  color: number;   // index into the palette
  alpha: number;
}

const clamp = (x: number, a = 0, b = 1) => (x < a ? a : x > b ? b : x);
const lerp = (a: number, b: number, k: number) => a + (b - a) * k;
const easeInOut = (x: number) => (x < 0.5 ? 2 * x * x : 1 - (-2 * x + 2) ** 2 / 2);
const easeOut = (x: number) => 1 - (1 - x) ** 3;
const rand = (a: number, b: number) => a + Math.random() * (b - a);
// eases toward a target at a rate that doesn't depend on the frame rate
const approach = (from: number, to: number, dt: number, tau: number) => from + (to - from) * (1 - Math.exp(-dt / tau));

function hexToRgb(hex: string, fallback: [number, number, number]): [number, number, number] {
  const m = /^#?([\da-f]{2})([\da-f]{2})([\da-f]{2})$/i.exec(hex.trim());
  return m ? [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)] : fallback;
}

// A soft dot, drawn once per color and stamped for every probe head.
function glowSprite([r, g, b]: [number, number, number]) {
  const c = document.createElement('canvas');
  c.width = c.height = 64;
  const g2 = c.getContext('2d')!;
  const gr = g2.createRadialGradient(32, 32, 0, 32, 32, 32);
  gr.addColorStop(0, 'rgba(255,255,255,1)');
  gr.addColorStop(0.16, `rgba(${r},${g},${b},0.95)`);
  gr.addColorStop(0.45, `rgba(${r},${g},${b},0.28)`);
  gr.addColorStop(1, `rgba(${r},${g},${b},0)`);
  g2.fillStyle = gr;
  g2.fillRect(0, 0, 64, 64);
  return c;
}

export function IntroAmbience() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const hero = canvas?.parentElement;
    if (!canvas || !hero || typeof IntersectionObserver === 'undefined' || typeof ResizeObserver === 'undefined') return;   // jsdom, in the tests
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    // The backlight's custom properties go on the player's wrapper, not the
    // whole hero, so updating them restyles as little as possible.
    const lightHost = hero.querySelector<HTMLElement>('.teaser') ?? hero;
    const motion = !matchMedia('(prefers-reduced-motion: reduce)').matches;

    // What the film last said, and what's on screen now, easing toward it.
    const target = { playing: false, full: false, light: 0, flash: 0, energy: 0, beat: 0, probes: 0 };
    const amb: number[][] = [[0, 0, 0], [0, 0, 0], [0, 0, 0]];
    const shown = { light: 0, probes: 0, amb: amb.map((c) => c.slice()) };
    let lastMessage = 0;
    let ringPulse = 0;
    const pulses: { at: number; level: number }[] = [];   // ring flashes timed to a beat still ahead
    let lastBeat = 0;
    let quietAfter = 0;   // no new probes after the final flash, until the film is sought back or played again
    const parts: Part[] = [];
    const credit = { hop: 0, arc: 0, link: 0 };

    // Geometry, in CSS px of the canvas: the rings' center and radii, and how
    // far a burst has to travel to clear every corner of the section.
    let cx = 0, cy = 0, far = 0, dpr = 1, sized = false;
    let radii = [330, 460, 550];
    let palette: [number, number, number][] = [];
    let sprites: HTMLCanvasElement[] = [];
    let dark = false;
    let visible = true;
    let raf = 0;
    let last = 0;
    let wasOn = false;

    const layout = () => {
      const rings = hero.querySelector<HTMLElement>('.hero-rings');
      const box = canvas.getBoundingClientRect();
      if (!rings || !box.width) return;
      const r = rings.getBoundingClientRect();
      const scale = r.width / RINGS_SIZE;
      cx = r.left + r.width / 2 - box.left;
      cy = r.top + r.height / 2 - box.top;
      radii = RING_INSETS.map((inset) => (RINGS_SIZE / 2 - inset) * scale);
      far = Math.max(Math.hypot(cx, cy), Math.hypot(box.width - cx, cy), Math.hypot(cx, box.height - cy), Math.hypot(box.width - cx, box.height - cy)) * 1.08;
      dpr = Math.min(devicePixelRatio || 1, 1.5);
      canvas.width = Math.round(box.width * dpr);
      canvas.height = Math.round(box.height * dpr);
      sized = true;
    };
    // The site's own amber and indigo, read when the film starts (the theme can change), plus the film's link cyan and white.
    const loadPalette = () => {
      const css = getComputedStyle(hero);
      dark = document.documentElement.dataset.theme === 'dark';
      palette = [
        hexToRgb(css.getPropertyValue('--amber'), [240, 168, 40]),
        hexToRgb(css.getPropertyValue('--indigo'), [84, 87, 229]),
        [143, 248, 255],
        [255, 244, 220],
      ];
      sprites = palette.map(glowSprite);
    };
    const release = () => {
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      canvas.width = canvas.height = 0;   // frees the backing store while the film isn't playing
      sized = false;
    };

    const ringColor = (ring: number) => (ring === 1 ? 1 : 0);   // the middle ring is the indigo one, as in styles.css
    const add = (p: Omit<Part, 't0'>, now: number) => {
      if (parts.length < MAX_PARTS) parts.push({ ...p, t0: now });
    };
    const hop = (now: number, from = Math.floor(rand(0, 2)), to = from + 1) => {
      const a0 = rand(0, Math.PI * 2);
      add({ kind: 'hop', dur: rand(1.3, 2.2), ring0: from, ring1: to, a0, a1: a0 + rand(-0.5, 0.5), color: ringColor(to), alpha: rand(0.5, 0.85) }, now);
    };
    const arc = (now: number) => {
      const ring = Math.floor(rand(0, 3)), a0 = rand(0, Math.PI * 2), dir = Math.random() < 0.5 ? -1 : 1;
      add({ kind: 'arc', dur: rand(1.6, 2.8), ring0: ring, ring1: ring, a0, a1: a0 + dir * rand(0.5, 1.1), color: ringColor(ring), alpha: rand(0.45, 0.8) }, now);
    };
    const link = (now: number) => {
      const ring0 = Math.floor(rand(0, 3));
      let ring1 = Math.floor(rand(0, 3));
      if (ring1 === ring0) ring1 = (ring0 + 1) % 3;
      const a0 = rand(0, Math.PI * 2);
      add({ kind: 'link', dur: rand(2.2, 3.4), ring0, ring1, a0, a1: a0 + rand(0.6, 2.2) * (Math.random() < 0.5 ? -1 : 1), color: Math.random() < 0.5 ? 0 : 2, alpha: rand(0.35, 0.6) }, now);
    };
    // Everything below starts at `at`, the moment the film says it happens
    // — usually now, for the final blast a little ahead.
    const burst = (at: number) => {
      add({ kind: 'wave', dur: 2.1, ring0: 0, ring1: 0, a0: 0, a1: 0, color: 3, alpha: 1 }, at);
      for (let i = 0; i < 56; i++) {
        const a = (i / 56) * Math.PI * 2 + rand(-0.04, 0.04);
        add({ kind: 'ray', dur: rand(1.2, 1.9), ring0: Math.floor(rand(0, 3)), ring1: 0, a0: a, a1: a, color: i % 3 === 0 ? 1 : i % 3 === 1 ? 0 : 3, alpha: rand(0.5, 0.95) }, at + rand(0, 0.25));
      }
      pulses.push({ at, level: 1 });
    };

    const onEvent = (kind: string, at: number) => {
      if (!motion) return;
      if (kind === 'deploy') { for (let i = 0; i < 5; i++) hop(at + i * 0.09, 0); pulses.push({ at, level: 0.3 }); }
      else if (kind === 'orbit') {   // into orbit: every ring busy at once
        for (let i = 0; i < 9; i++) hop(at + i * 0.07);
        for (let i = 0; i < 5; i++) arc(at + i * 0.1);
        pulses.push({ at, level: 0.45 });
      }
      else if (kind === 'land') { for (let i = 0; i < 4; i++) hop(at + i * 0.12); pulses.push({ at, level: 0.35 }); }
      else if (kind === 'link') { for (let i = 0; i < 3; i++) link(at + i * 0.2); }
      else if (kind === 'leap') { for (let i = 0; i < 10; i++) hop(at + i * 0.06, 2, 0); }   // light gathers in toward the Sun
      else if (kind === 'ring') pulses.push({ at, level: 1 });
      else if (kind === 'burst') { burst(at); quietAfter = at + 30; }
    };

    const onMessage = (e: MessageEvent) => {
      const frame = hero.querySelector<HTMLIFrameElement>('iframe.teaser-frame');
      if (e.origin !== location.origin || !frame || e.source !== frame.contentWindow) return;
      const d = e.data as IntroMessage;
      if (!d || d.type !== 'cloudish-intro') return;
      const now = performance.now() / 1000;
      if (d.playing !== undefined) target.playing = d.playing;
      if (d.full !== undefined) target.full = d.full;
      if (d.light !== undefined) target.light = d.light;
      if (d.flash !== undefined) target.flash = d.flash;
      if (d.energy !== undefined) target.energy = d.energy;
      if (d.beat !== undefined) target.beat = d.beat;
      if (d.probes !== undefined) {
        if (d.probes < 0.01 && target.probes >= 0.01) quietAfter = 0;   // the film was played again or sought back: probes may return
        target.probes = d.probes;
      }
      if (d.amb) d.amb.forEach((c, i) => { amb[i] = c; });
      lastMessage = now;
      for (const ev of d.events ?? []) onEvent(ev.kind, now + Math.max(0, ev.in || 0));
      wake();
    };

    // The film is "on" while it says it's playing, isn't fullscreen (then
    // the hero isn't what anyone is looking at), and keeps talking (it sends
    // a heartbeat even while buffering): a frame that goes silent — a frozen
    // tab, a crashed GL context — reads as paused. Turning off waits a
    // moment, so a pause that's over as soon as it starts (a stall, a quick
    // double press) doesn't flash the page's theme; turning on doesn't.
    let offSince = 0;
    const filmOn = (now: number) => {
      const said = target.playing && !target.full && now - lastMessage < 1.5;
      if (said) offSince = 0;
      else if (!offSince) offSince = now;
      return said || (wasOn && now - offSince < 0.6);
    };

    const step = () => {
      raf = 0;
      const now = performance.now() / 1000;
      const dt = Math.min(0.05, last ? now - last : 0.016);
      last = now;
      const on = filmOn(now);
      if (on !== wasOn) {
        wasOn = on;
        hero.classList.toggle('intro-playing', on);
        document.documentElement.classList.toggle('intro-playing', on);   // for the header, which lives outside the hero
        setThemeOverride(on ? 'dark' : null);
      }

      // The backlight: the picture's hue at full value, with its brightness
      // carried by the opacity — dark scenes glow faintly, bright ones warmer,
      // breathing a little with the music and lifting on the film's flashes.
      for (let i = 0; i < 3; i++) for (let k = 0; k < 3; k++) shown.amb[i][k] = approach(shown.amb[i][k], amb[i][k], dt, 0.45);
      const lum = shown.amb.reduce((s, [r, g, b]) => s + (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255, 0) / 3;
      const lightGoal = on ? clamp(target.light * (0.3 + 0.7 * clamp(lum * 3.2)) * (0.9 + 0.12 * target.energy + 0.05 * target.beat) + target.flash * 0.8) : 0;
      shown.light = approach(shown.light, lightGoal, dt, on ? 0.3 : 0.6);
      const hues = shown.amb.map((c) => {
        const m = Math.max(c[0], c[1], c[2]);
        return m < 3 ? '0 0 0' : c.map((v) => Math.round((v / m) * 255)).join(' ');
      });
      lightHost.style.setProperty('--intro-l', hues[0]);
      lightHost.style.setProperty('--intro-c', hues[1]);
      lightHost.style.setProperty('--intro-r', hues[2]);
      lightHost.style.setProperty('--intro-light', shown.light.toFixed(3));

      shown.probes = approach(shown.probes, on ? target.probes : 0, dt, 0.7);
      ringPulse = Math.max(0, ringPulse - dt * 1.1);
      for (let i = pulses.length - 1; i >= 0; i--) {
        if (pulses[i].at > now) continue;
        ringPulse = Math.max(ringPulse, pulses[i].level);
        pulses.splice(i, 1);
      }

      if (motion && visible) {
        if (!sized) { loadPalette(); layout(); }
        // New probes, faster as the film builds; one extra on strong beats.
        const L = shown.probes;
        if (on && L > 0.02 && now > quietAfter) {
          credit.hop += dt * (0.3 + 3.8 * L * L);
          credit.arc += dt * (0.2 + 2.2 * L);
          credit.link += dt * Math.max(0, L - 0.92) * 20;   // links only at the climax (the film keeps the orbit scene below 0.92)
          for (; credit.hop >= 1; credit.hop--) hop(now);
          for (; credit.arc >= 1; credit.arc--) arc(now);
          for (; credit.link >= 1; credit.link--) link(now);
          if (target.beat > 0.55 && lastBeat <= 0.55 && L > 0.2) hop(now);
        }
        lastBeat = target.beat;
        draw(now, L);
      } else {
        parts.length = 0;   // scrolled away: what was in flight is gone, not paused
        pulses.length = 0;
      }

      const busy = on || shown.light > 0.003 || shown.probes > 0.003 || ringPulse > 0.01 || parts.length > 0 || pulses.length > 0;
      if (busy) raf = requestAnimationFrame(step);
      else {
        last = 0;
        lightHost.style.setProperty('--intro-light', '0');
        if (sized) release();
      }
    };

    const polar = (r: number, a: number): [number, number] => [cx + Math.cos(a) * r, cy + Math.sin(a) * r];
    const stamp = (x: number, y: number, size: number, color: number, alpha: number) => {
      if (alpha <= 0.004) return;
      ctx.globalAlpha = Math.min(1, alpha);
      ctx.drawImage(sprites[color], x - size / 2, y - size / 2, size, size);
    };
    const rgba = (color: number, alpha: number) => {
      const [r, g, b] = palette[color];
      return `rgba(${r},${g},${b},${clamp(alpha).toFixed(3)})`;
    };

    const draw = (now: number, L: number) => {
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.globalCompositeOperation = dark ? 'lighter' : 'source-over';
      const k = dark ? 1 : 0.8;   // on the light theme the same light reads stronger

      // The orbits themselves wake a little with the activity, and ring out on the film's big beats.
      const ringA = (0.05 * L + 0.32 * ringPulse) * k;
      if (ringA > 0.004) {
        ctx.lineWidth = 1.2;
        radii.forEach((r, i) => {
          ctx.globalAlpha = 1;
          ctx.strokeStyle = rgba(ringColor(i), ringA);
          ctx.beginPath();
          ctx.arc(cx, cy, r, 0, Math.PI * 2);
          ctx.stroke();
        });
      }

      for (let i = parts.length - 1; i >= 0; i--) {
        const p = parts[i];
        const s = (now - p.t0) / p.dur;
        if (s >= 1) { parts.splice(i, 1); continue; }
        if (s < 0) continue;   // staggered: not yet
        const env = Math.sin(Math.PI * s) ** 0.6 * p.alpha * k;

        if (p.kind === 'hop' || p.kind === 'arc') {
          // A head with a fading tail, either jumping between rings or riding one.
          const at = (u: number): [number, number] => {
            const e = p.kind === 'hop' ? easeInOut(clamp(u)) : clamp(u);
            return polar(lerp(radii[p.ring0], radii[p.ring1], e), lerp(p.a0, p.a1, e));
          };
          const tail = p.kind === 'hop' ? 0.2 : 0.3;
          ctx.lineCap = 'round';
          ctx.lineWidth = 1.4;
          let [px, py] = at(s - tail);
          for (let j = 1; j <= 8; j++) {
            const [x, y] = at(s - tail + (tail * j) / 8);
            ctx.globalAlpha = 1;
            ctx.strokeStyle = rgba(p.color, env * (j / 8) * 0.7);
            ctx.beginPath();
            ctx.moveTo(px, py);
            ctx.lineTo(x, y);
            ctx.stroke();
            px = x; py = y;
          }
          stamp(px, py, 13, p.color, env);
        } else if (p.kind === 'link') {
          // A thread across the rings, drawn on, holding while a pulse runs along it, then let go.
          const [x0, y0] = polar(radii[p.ring0], p.a0), [x1, y1] = polar(radii[p.ring1], p.a1);
          const qx = lerp((x0 + x1) / 2, cx, 0.35), qy = lerp((y0 + y1) / 2, cy, 0.35);
          const pt = (u: number): [number, number] => [(1 - u) ** 2 * x0 + 2 * (1 - u) * u * qx + u * u * x1, (1 - u) ** 2 * y0 + 2 * (1 - u) * u * qy + u * u * y1];
          const drawn = easeOut(clamp(s / 0.3));
          const fade = 1 - clamp((s - 0.6) / 0.4);
          ctx.globalAlpha = 1;
          ctx.lineWidth = 1;
          ctx.strokeStyle = rgba(p.color, 0.5 * p.alpha * fade * k);
          ctx.beginPath();
          ctx.moveTo(x0, y0);
          for (let j = 1; j <= 24; j++) {
            const [x, y] = pt((drawn * j) / 24);
            ctx.lineTo(x, y);
          }
          ctx.stroke();
          const [hx, hy] = pt(((now - p.t0) * 0.9) % 1);
          stamp(hx, hy, 10, p.color, p.alpha * fade * k * (s > 0.3 ? 1 : 0));
          stamp(x0, y0, 9, p.color, p.alpha * 0.6 * fade * k);
          stamp(x1, y1, 9, p.color, p.alpha * 0.6 * fade * k);
        } else if (p.kind === 'ray') {
          // Light leaving the system: from a ring out past the section's edges.
          const e = easeOut(s);
          const r = lerp(radii[p.ring0], far, e), r0 = Math.max(radii[p.ring0], r - 140 * (1 - s * 0.5));
          const [x0, y0] = polar(r0, p.a0), [x1, y1] = polar(r, p.a0);
          const g = ctx.createLinearGradient(x0, y0, x1, y1);
          g.addColorStop(0, rgba(p.color, 0));
          g.addColorStop(1, rgba(p.color, (1 - s) ** 1.2 * p.alpha * k));
          ctx.globalAlpha = 1;
          ctx.strokeStyle = g;
          ctx.lineWidth = 1.6;
          ctx.beginPath();
          ctx.moveTo(x0, y0);
          ctx.lineTo(x1, y1);
          ctx.stroke();
          stamp(x1, y1, 12, p.color, (1 - s) ** 1.2 * p.alpha * k);
        } else if (p.kind === 'wave') {
          // One soft ring of light sweeping out over everything, and a brief lift of the whole section.
          const e = easeOut(s);
          const r = lerp(radii[0] * 0.8, far * 1.05, e), w = lerp(40, 140, e);
          const a = (1 - s) ** 1.6 * 0.55 * k;
          const g = ctx.createRadialGradient(cx, cy, Math.max(0, r - w), cx, cy, r + w);
          g.addColorStop(0, rgba(3, 0));
          g.addColorStop(0.5, rgba(3, a));
          g.addColorStop(1, rgba(3, 0));
          ctx.globalAlpha = 1;
          ctx.fillStyle = g;
          ctx.fillRect(0, 0, canvas.width / dpr, canvas.height / dpr);
          const lift = (1 - clamp(s / 0.35)) ** 2 * 0.16 * k;
          if (lift > 0.004) {
            const g2 = ctx.createRadialGradient(cx, cy, 0, cx, cy, far);
            g2.addColorStop(0, rgba(3, lift));
            g2.addColorStop(1, rgba(3, 0));
            ctx.fillStyle = g2;
            ctx.fillRect(0, 0, canvas.width / dpr, canvas.height / dpr);
          }
        }
      }
      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = 'source-over';
    };

    const wake = () => {
      if (!raf) raf = requestAnimationFrame(step);
    };

    const visibility = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      if (!visible && sized) release();
      if (visible) wake();
    });
    visibility.observe(hero);
    const resize = new ResizeObserver(() => { if (sized) layout(); });
    resize.observe(hero);
    addEventListener('message', onMessage);

    return () => {
      removeEventListener('message', onMessage);
      visibility.disconnect();
      resize.disconnect();
      if (raf) cancelAnimationFrame(raf);
      hero.classList.remove('intro-playing');
      document.documentElement.classList.remove('intro-playing');
      setThemeOverride(null);
    };
  }, []);

  return <canvas className="hero-probes" ref={canvasRef} aria-hidden="true" />;
}

export default IntroAmbience;
